import type { DailyHealthMetricName, DailyHealthRecord, DailyMetricTrend, DailyTrendWindow } from "./dailyHealthTypes";

const WINDOW_DAYS: DailyTrendWindow[] = [7, 28, 90];

const METRIC_THRESHOLDS: Record<DailyHealthMetricName, number> = {
  bodyWeightKg: 4,
  bodyFatPercentage: 4,
  leanBodyMassKg: 4,
  waistCircumferenceCm: 4,
  bmi: 4,
  hrvSdnnMs: 5,
  hrvRmssdMs: 5,
  restingHeartRateBpm: 5,
  respiratoryRateBpm: 5,
  wristTemperatureCelsius: 5,
  oxygenSaturationPercentage: 5,
  walkingHeartRateAverageBpm: 5,
  heartRateRecoveryOneMinuteBpm: 4,
  vo2MaxMlKgMin: 3,
  systolicBloodPressure: 4,
  diastolicBloodPressure: 4,
  steps: 5,
  activeEnergyKcal: 5,
  basalEnergyKcal: 5,
  exerciseMinutes: 5,
  standHours: 5,
  walkingRunningDistanceMeters: 5,
  sleepDurationMinutes: 5
};

function metricValue(record: DailyHealthRecord, metric: DailyHealthMetricName): number | null {
  switch (metric) {
    case "bodyWeightKg": return record.bodyComposition.bodyWeightKg;
    case "bodyFatPercentage": return record.bodyComposition.bodyFatPercentage;
    case "leanBodyMassKg": return record.bodyComposition.leanBodyMassKg;
    case "waistCircumferenceCm": return record.bodyComposition.waistCircumferenceCm;
    case "bmi": return record.bodyComposition.bmi;
    case "hrvSdnnMs": return record.recovery.hrvSdnnMs;
    case "hrvRmssdMs": return record.recovery.hrvRmssdMs;
    case "restingHeartRateBpm": return record.recovery.restingHeartRateBpm;
    case "respiratoryRateBpm": return record.recovery.respiratoryRateBpm;
    case "wristTemperatureCelsius": return record.recovery.wristTemperatureCelsius;
    case "oxygenSaturationPercentage": return record.recovery.oxygenSaturationPercentage;
    case "walkingHeartRateAverageBpm": return record.cardiovascular.walkingHeartRateAverageBpm;
    case "heartRateRecoveryOneMinuteBpm": return record.cardiovascular.heartRateRecoveryOneMinuteBpm;
    case "vo2MaxMlKgMin": return record.cardiovascular.vo2MaxMlKgMin;
    case "systolicBloodPressure": return record.cardiovascular.systolicBloodPressure;
    case "diastolicBloodPressure": return record.cardiovascular.diastolicBloodPressure;
    case "steps": return record.activityContext.steps;
    case "activeEnergyKcal": return record.activityContext.activeEnergyKcal;
    case "basalEnergyKcal": return record.activityContext.basalEnergyKcal;
    case "exerciseMinutes": return record.activityContext.exerciseMinutes;
    case "standHours": return record.activityContext.standHours;
    case "walkingRunningDistanceMeters": return record.activityContext.walkingRunningDistanceMeters;
    case "sleepDurationMinutes": return record.sleep.totalSleepMinutes;
    default: return null;
  }
}

function average(values: number[]): number {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }
  return sorted[middle];
}

function confidence(sampleCount: number, threshold: number): DailyMetricTrend["confidence"] {
  if (sampleCount >= threshold * 1.6) return "high";
  if (sampleCount >= threshold) return "medium";
  return "low";
}

function trendDirection(recent: number, baselineMin: number, baselineMax: number): DailyMetricTrend["direction"] {
  if (recent > baselineMax) return "above";
  if (recent < baselineMin) return "below";
  return "within";
}

function trendLanguage(direction: DailyMetricTrend["direction"]): DailyMetricTrend["language"] {
  if (direction === "above") return "above your recent range";
  if (direction === "below") return "below your recent range";
  if (direction === "within") return "within your recent range";
  return "insufficient data";
}

function percentChange(recent: number, baseline: number): number | null {
  if (baseline === 0) return null;
  return ((recent - baseline) / Math.abs(baseline)) * 100;
}

export function buildDailyMetricTrends(records: DailyHealthRecord[], metric: DailyHealthMetricName): DailyMetricTrend[] {
  const sorted = [...records].sort((left, right) => left.localDate.localeCompare(right.localDate));
  const values = sorted
    .map(record => ({ date: record.localDate, value: metricValue(record, metric) }))
    .filter((entry): entry is { date: string; value: number } => entry.value !== null && Number.isFinite(entry.value));

  return WINDOW_DAYS.map(windowDays => {
    const windowValues = values.slice(-windowDays);
    const numeric = windowValues.map(entry => entry.value);
    const threshold = METRIC_THRESHOLDS[metric];

    if (numeric.length < threshold) {
      return {
        metric,
        windowDays,
        sampleCount: numeric.length,
        mean: null,
        median: null,
        minimum: null,
        maximum: null,
        recentValue: numeric.at(-1) ?? null,
        percentChange: null,
        direction: "insufficient",
        confidence: "low",
        language: "insufficient data"
      } satisfies DailyMetricTrend;
    }

    const recent = numeric[numeric.length - 1];
    const baseline = numeric.slice(0, -1);
    const baselineForRange = baseline.length > 0 ? baseline : numeric;
    const baselineMean = average(baselineForRange);
    const minimum = Math.min(...baselineForRange);
    const maximum = Math.max(...baselineForRange);
    const direction = trendDirection(recent, minimum, maximum);

    return {
      metric,
      windowDays,
      sampleCount: numeric.length,
      mean: average(numeric),
      median: median(numeric),
      minimum: Math.min(...numeric),
      maximum: Math.max(...numeric),
      recentValue: recent,
      percentChange: percentChange(recent, baselineMean),
      direction,
      confidence: confidence(numeric.length, threshold),
      language: trendLanguage(direction)
    } satisfies DailyMetricTrend;
  });
}
